import React, { useState } from "react";
import {
  FaFileCode,
  FaFilePdf,
  FaFileAlt,
  FaMedium,
  FaWordpress,
  FaDev,
  FaHashtag,
  FaLinkedin,
  FaGithub,
  FaLightbulb,
} from "react-icons/fa";
import MonoButton from "../kits/DesignKit/MonoButton";
import ModalWrapper from "./utils/ModalWrapper";
import PaperBG from "../kits/DesignKit/PaperBG";

function ZenEditorExportModal({ markdown, onClose, inline = false }) {
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [toast, setToast] = useState(null);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Convert Markdown to HTML
  const markdownToHTML = (md) => {
    let html = md;

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');

    // Italic
    html = html.replace(/\_(.*?)\_/gim, '<em>$1</em>');
    html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');

    // Strikethrough
    html = html.replace(/\~\~(.*?)\~\~/gim, '<del>$1</del>');

    // Code inline
    html = html.replace(/`([^`]+)`/gim, '<code>$1</code>');

    // Code blocks
    html = html.replace(/```([a-z]*)\n([\s\S]*?)```/gim, '<pre><code class="language-$1">$2</code></pre>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^\)]+)\)/gim, '<a href="$2">$1</a>');

    // Images
    html = html.replace(/\!\[([^\]]*)\]\(([^\)]+)\)/gim, '<img src="$2" alt="$1" />');

    // Lists
    html = html.replace(/^\* (.+)$/gim, '<li>$1</li>');
    html = html.replace(/^\- (.+)$/gim, '<li>$1</li>');
    html = html.replace(/(<li>.*<\/li>)/gims, '<ul>$1</ul>');

    // Blockquotes
    html = html.replace(/^\> (.+)$/gim, '<blockquote>$1</blockquote>');

    // HR
    html = html.replace(/^\-\-\-$/gim, '<hr />');

    // Paragraphs
    html = html.replace(/\n\n/g, '</p><p>');
    html = '<p>' + html + '</p>';

    return html;
  };

  // Export as HTML
  const exportAsHTML = () => {
    setExporting(true);
    setExportType('html');

    const htmlContent = markdownToHTML(markdown);
    const fullHTML = `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ZenEditor Export</title>
  <style>
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      max-width: 800px;
      margin: 40px auto;
      padding: 20px;
      line-height: 1.6;
      color: #000;
    }
    h1, h2, h3 {
      color: #000;
      margin-top: 1.5em;
      font-weight: bold;
    }
    h1 {
      font-size: 2em;
      border-bottom: 2px solid #000;
      padding-bottom: 0.3em;
    }
    h2 { font-size: 1.5em; }
    h3 { font-size: 1.17em; }
    code {
      background: #f5f5f5;
      padding: 2px 6px;
      border: 1px solid #ddd;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      color: #000;
    }
    pre {
      background: #f5f5f5;
      color: #000;
      padding: 15px;
      border: 1px solid #ddd;
      border-radius: 5px;
      overflow-x: auto;
    }
    pre code {
      background: transparent;
      color: #000;
      padding: 0;
      border: none;
    }
    blockquote {
      border-left: 4px solid #000;
      padding-left: 20px;
      margin-left: 0;
      font-style: italic;
      color: #333;
    }
    a {
      color: #000;
      text-decoration: underline;
    }
    a:hover { text-decoration: none; }
    img { max-width: 100%; height: auto; }
    ul, ol { padding-left: 30px; }
    li { margin-bottom: 0.5em; }
    hr { border: none; border-top: 2px solid #000; margin: 2em 0; }
    strong { font-weight: bold; }
  </style>
</head>
<body>
  ${htmlContent}
  <footer style="margin-top: 3em; padding-top: 2em; border-top: 1px solid #000; color: #666; font-size: 0.9em;">
    <p>Erstellt mit <a href="https://zeneditor.com" style="color: #000;">ZenEditor</a></p>
  </footer>
</body>
</html>`;

    const blob = new Blob([fullHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeneditor-export-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setExporting(false);
      setExportType(null);
    }, 1000);
  };

  // Check if mobile device
  const isMobile = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  };

  // Check if PWA (standalone mode)
  const isPWA = () => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true;
  };

  // Export as PDF (using browser print)
  const exportAsPDF = () => {
    setExporting(true);
    setExportType('pdf');

    const htmlContent = markdownToHTML(markdown);

    // On mobile/PWA: use iframe approach to avoid navigation issues
    if (isMobile() || isPWA()) {
      // Create a hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(`
        <!DOCTYPE html>
        <html lang="de">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>ZenEditor PDF Export</title>
          <style>
            @media print {
              body { margin: 0; }
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              max-width: 210mm;
              margin: 20mm auto;
              padding: 20px;
              line-height: 1.6;
              color: #000;
            }
            h1, h2, h3 {
              color: #000;
              margin-top: 1.5em;
              page-break-after: avoid;
              font-weight: bold;
            }
            h1 {
              font-size: 2em;
              border-bottom: 2px solid #000;
              padding-bottom: 0.3em;
            }
            h2 { font-size: 1.5em; }
            h3 { font-size: 1.17em; }
            code {
              background: #f5f5f5;
              padding: 2px 6px;
              border: 1px solid #ddd;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
              color: #000;
            }
            pre {
              background: #f5f5f5;
              color: #000;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 5px;
              overflow-x: auto;
              page-break-inside: avoid;
            }
            pre code {
              background: transparent;
              color: #000;
              padding: 0;
              border: none;
            }
            blockquote {
              border-left: 4px solid #000;
              padding-left: 20px;
              margin-left: 0;
              font-style: italic;
              color: #333;
              page-break-inside: avoid;
            }
            img { max-width: 100%; height: auto; page-break-inside: avoid; }
            ul, ol { padding-left: 30px; }
            li { margin-bottom: 0.5em; }
            hr { border: none; border-top: 2px solid #000; margin: 2em 0; }
            a { color: #000; text-decoration: underline; }
            strong { font-weight: bold; }
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
        </html>
      `);
      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Clean up iframe after print dialog closes
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);

        setExporting(false);
        setExportType(null);
        showToast('Druck-Dialog geöffnet', 'success');
      }, 500);

      return;
    }

    // Desktop: use new window approach
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="de">
      <head>
        <meta charset="UTF-8">
        <title>ZenEditor PDF Export</title>
        <style>
          @media print {
            body { margin: 0; }
            .no-print { display: none !important; }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            max-width: 210mm;
            margin: 20mm auto;
            padding: 20px;
            line-height: 1.6;
            color: #000;
          }
          h1, h2, h3 {
            color: #000;
            margin-top: 1.5em;
            page-break-after: avoid;
            font-weight: bold;
          }
          h1 {
            font-size: 2em;
            border-bottom: 2px solid #000;
            padding-bottom: 0.3em;
          }
          h2 { font-size: 1.5em; }
          h3 { font-size: 1.17em; }
          code {
            background: #f5f5f5;
            padding: 2px 6px;
            border: 1px solid #ddd;
            border-radius: 3px;
            font-family: 'Courier New', monospace;
            color: #000;
          }
          pre {
            background: #f5f5f5;
            color: #000;
            padding: 15px;
            border: 1px solid #ddd;
            border-radius: 5px;
            overflow-x: auto;
            page-break-inside: avoid;
          }
          pre code {
            background: transparent;
            color: #000;
            padding: 0;
            border: none;
          }
          blockquote {
            border-left: 4px solid #000;
            padding-left: 20px;
            margin-left: 0;
            font-style: italic;
            color: #333;
            page-break-inside: avoid;
          }
          img { max-width: 100%; height: auto; page-break-inside: avoid; }
          ul, ol { padding-left: 30px; }
          li { margin-bottom: 0.5em; }
          hr { border: none; border-top: 2px solid #000; margin: 2em 0; }
          a { color: #000; text-decoration: underline; }
          strong { font-weight: bold; }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      setExporting(false);
      setExportType(null);
    }, 500);
  };

  // Export as Markdown
  const exportAsMarkdown = () => {
    setExporting(true);
    setExportType('md');

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeneditor-export-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setExporting(false);
      setExportType(null);
    }, 1000);
  };

  // Export as Plain Text
  const exportAsText = () => {
    setExporting(true);
    setExportType('txt');

    // Strip markdown formatting for plain text
    let plainText = markdown;
    plainText = plainText.replace(/#{1,6}\s/g, ''); // Remove headers
    plainText = plainText.replace(/\*\*([^*]+)\*\*/g, '$1'); // Remove bold
    plainText = plainText.replace(/\*([^*]+)\*/g, '$1'); // Remove italic
    plainText = plainText.replace(/\_([^_]+)\_/g, '$1'); // Remove italic
    plainText = plainText.replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1'); // Links to text
    plainText = plainText.replace(/```[a-z]*\n/g, ''); // Remove code fence start
    plainText = plainText.replace(/```/g, ''); // Remove code fence end
    plainText = plainText.replace(/`([^`]+)`/g, '$1'); // Remove inline code
    plainText = plainText.replace(/^\>\s/gm, ''); // Remove blockquotes
    plainText = plainText.replace(/^\-\s/gm, '• '); // List bullets

    const blob = new Blob([plainText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zeneditor-export-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setExporting(false);
      setExportType(null);
    }, 1000);
  };

  // Copy only to clipboard (without opening platform)
  const copyForPlatform = (platform) => {
    setExporting(true);
    setExportType(platform);

    let content = markdown;

    // Platform-specific formatting
    if (platform === 'medium') {
      content = markdown;
    } else if (platform === 'dev') {
      content = `---
title: Dein Titel hier
published: false
tags: javascript, webdev, tutorial
---

${markdown}`;
    } else if (platform === 'hashnode') {
      content = markdown;
    } else if (platform === 'linkedin') {
      // LinkedIn needs plain text
      content = markdown
        .replace(/#{1,6}\s/g, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
    } else if (platform === 'wordpress') {
      content = markdown;
    } else if (platform === 'github') {
      content = markdown;
    }

    navigator.clipboard.writeText(content).then(() => {
      setExporting(false);
      setExportType(null);
      showToast(`Inhalt für ${platform.toUpperCase()} kopiert!`, 'success');
    }).catch((err) => {
      console.error('Clipboard error:', err);
      showToast('Fehler beim Kopieren. Bitte manuell kopieren.', 'error');
      setExporting(false);
      setExportType(null);
    });
  };

  // Open posting dialog for platforms
  const postToPlatform = (platform) => {
    setExporting(true);
    setExportType(platform);

    const urls = {
      medium: 'https://medium.com/new-story',
      wordpress: 'https://wordpress.com/post',
      dev: 'https://dev.to/new',
      hashnode: 'https://hashnode.com/create-story',
      linkedin: 'https://www.linkedin.com/post/new',
      github: 'https://gist.github.com/',
    };

    let content = markdown;

    // Platform-specific formatting
    if (platform === 'medium') {
      // Medium supports Markdown
      content = markdown;
    } else if (platform === 'dev') {
      // DEV.to supports Markdown with front matter
      content = `---
title: Dein Titel hier
published: false
tags: javascript, webdev, tutorial
---

${markdown}`;
    } else if (platform === 'hashnode') {
      // Hashnode supports Markdown
      content = markdown;
    } else if (platform === 'linkedin') {
      // LinkedIn needs plain text
      content = markdown
        .replace(/#{1,6}\s/g, '')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');
    }

    // WICHTIG: Fenster ZUERST öffnen (vor async clipboard operation)
    // Sonst wird es vom Pop-up-Blocker blockiert!
    const newWindow = window.open(urls[platform], '_blank');

    // Dann Content in Zwischenablage kopieren
    navigator.clipboard.writeText(content).then(() => {
      setExporting(false);
      setExportType(null);

      // Zeige nicht-blockierende Toast-Notification
      showToast(`Content für ${platform.toUpperCase()} kopiert! Tab geöffnet - drücke Cmd+V zum Einfügen.`, 'success');
    }).catch((err) => {
      console.error('Clipboard error:', err);
      showToast(`${platform.toUpperCase()}-Tab geöffnet. Bitte manuell kopieren (Cmd+A, Cmd+C).`, 'warning');
      setExporting(false);
      setExportType(null);
    });
  };

  const content = (
    <>
      <PaperBG className="max-w-full mx-auto rounded-xl p-8">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-brand">Export & Veröffentlichen</h2>
          <p className="text-xs text-gray-600 mt-1">
            Exportiere deinen Content oder teile ihn direkt auf Plattformen
          </p>
        </div>
        <div className="border-t border-brand pt-6"></div>
        {/* File Export Section */}
        <div className="mb-6">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-800">
            <FaFileAlt className="text-brand" />
            Datei-Export
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="relative h-[140px]">
              <MonoButton
                onClick={exportAsHTML}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaFileCode size={40} className="text-brand group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">HTML</span>
              </MonoButton>
              {exportType === 'html' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Exportiert</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={exportAsPDF}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaFilePdf size={40} className="text-brand group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">PDF</span>
              </MonoButton>
              {exportType === 'pdf' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Geöffnet</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={exportAsMarkdown}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaFileAlt size={40} className="text-brand group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">Markdown</span>
              </MonoButton>
              {exportType === 'md' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Exportiert</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={exportAsText}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaFileAlt size={40} className="text-brand group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">Text</span>
              </MonoButton>
              {exportType === 'txt' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Exportiert</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Platform Publishing Section */}
        <div className="border-t border-brand pt-6">
          <h3 className="text-base font-semibold mb-3 flex items-center gap-2 text-gray-800">
            <FaMedium className="text-brand" />
            Direkt Veröffentlichen
          </h3>
          <p className="text-xs text-gray-600 mb-4">
            Klicke auf eine Plattform, um den Content zu kopieren und die Posting-Seite zu öffnen
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="relative h-[140px]">
              <MonoButton
                onClick={() => postToPlatform('medium')}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaMedium size={40} className="text-slate-50 group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">Medium</span>
              </MonoButton>
              {exportType === 'medium' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Kopiert</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={() => postToPlatform('wordpress')}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaWordpress size={40} className="text-slate-50 group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">WordPress</span>
              </MonoButton>
              {exportType === 'wordpress' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Kopiert</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={() => postToPlatform('dev')}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaDev size={40} className="text-slate-50 group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">DEV.to</span>
              </MonoButton>
              {exportType === 'dev' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Kopiert</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={() => postToPlatform('hashnode')}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaHashtag size={40} className="text-slate-50 group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">Hashnode</span>
              </MonoButton>
              {exportType === 'hashnode' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Kopiert</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={() => postToPlatform('linkedin')}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaLinkedin size={40} className="text-slate-50 group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">LinkedIn</span>
              </MonoButton>
              {exportType === 'linkedin' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Kopiert</span>
                </div>
              )}
            </div>

            <div className="relative h-[140px]">
              <MonoButton
                onClick={() => postToPlatform('github')}
                disabled={exporting}
                className="w-full flex flex-col items-center justify-center gap-3 p-6 text-sm hover:bg-brand h-full group"
              >
                <FaGithub size={40} className="text-slate-50 group-hover:text-slate-50 transition-colors" />
                <span className="font-bold">GitHub Gist</span>
              </MonoButton>
              {exportType === 'github' && (
                <div className="absolute inset-0 bg-slate-800 flex items-center justify-center animate-fade-in rounded-md z-10 pointer-events-none">
                  <span className="text-slate-50 font-bold text-sm">✓ Kopiert</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6  bg-[#d9d4c5] border border-brand  rounded px-4 py-3">
          <p className="text-xs text-slate-800 flex items-start gap-2">
            <FaLightbulb className="text-brand mt-0.5 flex-shrink-0" />
            <span><strong>Tipp:</strong> Für beste Ergebnisse auf Plattformen wie Medium und DEV.to
            verwende Markdown-Formatierung mit Überschriften, Listen und Code-Blöcken.</span>
          </p>
        </div>
      </PaperBG>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-4 right-4 z-[60] animate-slide-up">
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 ${
            toast.type === 'success' ? 'bg-green-600' :
            toast.type === 'error' ? 'bg-red-600' :
            'bg-orange-600'
          }`}>
            <span className="text-slate-50 text-sm font-medium">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '✗' : '⚠️'}
            </span>
            <span className="text-slate-50 text-sm">{toast.message}</span>
          </div>
        </div>
      )}
    </>
  );

  if (inline) {
    return <div>{content}</div>;
  }

  return <ModalWrapper onClose={onClose}>{content}</ModalWrapper>;
}

export default ZenEditorExportModal;
